/** @format */

import React, { FC, useEffect, useState } from "react";
import { Form } from "antd";
import UIModal from "../../../ui/Modal";
import { UIInput } from "../../../ui/Input";
import { UIText } from "../../../ui/Text";
import { UIButton } from "../../../ui/Button";
import { TIntermittentCommunicationDetail } from "../../../../redux-modules/declarations/maps/intermittentCommunications";
import {
  apiIntermittentUpdateInpsCode,
  intermittentValidationMessage,
} from "../../../../api/apiIntermittentCommunications";
import { showNotification } from "../../../../utils/showNotification";
import { fmtDay, fmtShift, fullName } from "../constants";

interface Props {
  communication?: TIntermittentCommunicationDetail;
  visible: boolean;
  onCancel: () => void;
  onSaved: (updated: TIntermittentCommunicationDetail) => void;
}

/**
 * Inserimento/correzione del codice comunicazione INPS dal pannello della comunicazione.
 * Il codice viene salvato nell'anagrafica del dipendente.
 */
export const InpsCodeModal: FC<Props> = ({ communication, visible, onCancel, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible) form.setFieldsValue({ inps_code: communication?.inps_code || "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, communication?.id]);

  if (!communication) return <></>;

  const isNew = !communication.inps_code;

  const handleOk = async (): Promise<void> => {
    let values: { inps_code: string };
    try {
      values = await form.validateFields();
    } catch (e) {
      return;
    }

    setSaving(true);
    try {
      const updated = await apiIntermittentUpdateInpsCode(communication.id, values.inps_code.replace(/\s+/g, ""));
      showNotification(
        "success",
        "Codice INPS salvato",
        `${fullName(updated)}: codice salvato in anagrafica${
          communication.status === "blocked" && updated.status !== "blocked" ? ", comunicazione sbloccata" : ""
        }.`,
      );
      onSaved(updated);
    } catch (err) {
      showNotification("error", "Codice INPS", intermittentValidationMessage(err, "Impossibile salvare il codice INPS."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <UIModal
      visible={visible}
      title={isNew ? "Inserisci codice comunicazione INPS" : "Modifica codice comunicazione INPS"}
      onCancel={onCancel}
      destroyOnClose
      footer={[
        <UIButton key="cancel" onClick={onCancel}>
          Indietro
        </UIButton>,
        <UIButton key="ok" type="primary" loading={saving} onClick={handleOk} data-cy="intermittent-inps-save">
          Salva
        </UIButton>,
      ]}
    >
      <div style={{ marginBottom: 12 }}>
        <UIText strong>{fullName(communication)}</UIText>
        <br />
        <UIText type="secondary">
          {communication.fiscal_code || "-"} · {fmtDay(communication.work_date)} · turno {fmtShift(communication)}
        </UIText>
      </div>
      <p style={{ marginBottom: 16 }}>
        Il codice viene salvato nell'<strong>anagrafica del dipendente</strong> e usato per tutte le sue comunicazioni
        non ancora inviate.
        {communication.status === "blocked" && " Se era l'unico dato mancante, la comunicazione si sblocca subito."}
      </p>
      <Form form={form} layout="vertical" onFinish={handleOk}>
        <Form.Item
          name="inps_code"
          label="Codice comunicazione INPS"
          normalize={(v: string): string => (v || "").replace(/\s+/g, "")}
          rules={[
            { required: true, message: "Inserisci il codice comunicazione INPS" },
            { pattern: /^[A-Za-z0-9]+$/, message: "Il codice può contenere solo lettere e numeri" },
            { max: 30, message: "Massimo 30 caratteri" },
          ]}
          style={{ marginBottom: 0 }}
        >
          <UIInput autoFocus maxLength={30} placeholder="Es. 1608226340839731" />
        </Form.Item>
      </Form>
    </UIModal>
  );
};
